import React, { useState, useEffect } from 'react';
import { processStepsApi } from '@/services/api';
import { toast } from 'sonner';
import { Save, Loader2 } from 'lucide-react';

const AdminProcessSteps = () => {
  const [steps, setSteps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null); // step id being saved

  useEffect(() => {
    processStepsApi.getAll()
      .then((res) => setSteps(res.data))
      .catch(() => toast.error('Failed to load process steps'))
      .finally(() => setLoading(false));
  }, []);

  const handleChange = (id, field, value) => {
    setSteps((prev) =>
      prev.map((s) => (s.id === id ? { ...s, [field]: value } : s))
    );
  };

  const handleSave = async (step) => {
    setSaving(step.id);
    try {
      const { id, created_at, ...payload } = step;
      await processStepsApi.update(step.id, payload);
      toast.success('Step saved successfully');
    } catch {
      toast.error('Failed to save step');
    } finally {
      setSaving(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="animate-spin text-gold" size={36} />
      </div>
    );
  }

  return (
    <div>
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-white mb-1">Process Steps</h1>
        <p className="text-white/50 text-sm">Edit the "How We Work" section steps shown on the homepage.</p>
      </div>

      <div className="grid gap-6">
        {steps.map((step, index) => (
          <div key={step.id} className="bg-brand-panel border border-white/10 p-6">
            <div className="flex items-center gap-3 mb-5">
              <span className="w-8 h-8 rounded-full bg-gold/20 text-gold flex items-center justify-center text-sm font-bold">
                {String(index + 1).padStart(2, '0')}
              </span>
              <h3 className="text-white font-semibold">{step.title_en}</h3>
            </div>

            <div className="grid md:grid-cols-2 gap-5">
              {/* English */}
              <div className="space-y-4">
                <p className="text-xs text-white/40 uppercase tracking-widest mb-2">English</p>
                <div>
                  <label className="block text-white/60 text-sm mb-1">Title (EN)</label>
                  <input
                    type="text"
                    value={step.title_en}
                    onChange={(e) => handleChange(step.id, 'title_en', e.target.value)}
                    className="w-full bg-white/5 border border-white/10 text-white px-4 py-2.5 focus:outline-none focus:border-gold/50 transition-colors"
                  />
                </div>
                <div>
                  <label className="block text-white/60 text-sm mb-1">Description (EN)</label>
                  <textarea
                    rows={3}
                    value={step.description_en}
                    onChange={(e) => handleChange(step.id, 'description_en', e.target.value)}
                    className="w-full bg-white/5 border border-white/10 text-white px-4 py-2.5 focus:outline-none focus:border-gold/50 transition-colors resize-none"
                  />
                </div>
              </div>

              {/* Arabic */}
              <div className="space-y-4" dir="rtl">
                <p className="text-xs text-white/40 uppercase tracking-widest mb-2">عربي</p>
                <div>
                  <label className="block text-white/60 text-sm mb-1">العنوان</label>
                  <input
                    type="text"
                    value={step.title_ar}
                    onChange={(e) => handleChange(step.id, 'title_ar', e.target.value)}
                    className="w-full bg-white/5 border border-white/10 text-white px-4 py-2.5 focus:outline-none focus:border-gold/50 transition-colors"
                  />
                </div>
                <div>
                  <label className="block text-white/60 text-sm mb-1">الوصف</label>
                  <textarea
                    rows={3}
                    value={step.description_ar}
                    onChange={(e) => handleChange(step.id, 'description_ar', e.target.value)}
                    className="w-full bg-white/5 border border-white/10 text-white px-4 py-2.5 focus:outline-none focus:border-gold/50 transition-colors resize-none"
                  />
                </div>
              </div>
            </div>

            <div className="flex justify-end mt-5">
              <button
                onClick={() => handleSave(step)}
                disabled={saving === step.id}
                className="flex items-center gap-2 bg-gold text-charcoal px-5 py-2.5 font-semibold hover:bg-gold/90 transition-colors disabled:opacity-50"
              >
                {saving === step.id ? (
                  <Loader2 size={16} className="animate-spin" />
                ) : (
                  <Save size={16} />
                )}
                Save Step
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default AdminProcessSteps;
